import React, { useEffect, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import useAxios from "../../../hooks/useAxios";
import "./ClassManagementPage.css";
import {
  Box,
  CircularProgress,
  Container,
  Typography,
  Button,
  Grid,
  Tooltip,
  IconButton,
} from "@mui/material";
import Header from "../../../components/Header/Header";
import ContentHeader from "../../../components/common/ContentHeader/ContentHeader";
import QuizCard from "../../../components/QuizComponents/Cards/QuizCard/QuizCard";
import ConfirmationModal from "../../../components/common/ConfirmationModal/ConfirmationModal";
import { useNavigate, useParams } from "react-router-dom";
import { useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import GradeBookTable from "../../../components/Reporting/GradeBookTable/GradeBookTable";
import ReportModal from "../../../components/Reporting/Modals/ReportModal/ReportModal";

const navLinks = [
];

function ClassManagementPage() {
  const navigate = useNavigate();
  const { classId } = useParams();
  const axiosInstance = useAxios();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [classData, setClassData] = useState(null);
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [title, setTitle] = useState('Copy ID')
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [currentReport, setCurrentReport] = useState(null);
  const [studentsData, setStudentsData] = useState({});
  const [quizzesForTable, setQuizzesForTable] = useState();

  // Determine initial display count based on screen size
  let initialDisplayCount = 3;
  if (isSmallScreen) {
    initialDisplayCount = 1;
  } else if (isMediumScreen) {
    initialDisplayCount = 2;
  }

  const fetchQuizzes = async () => {
    try {
      const response = await axiosInstance.get(`/classes/${classId}`);
      if (response.data.success) {
        const classInfo = response.data.class;
        const quizzesArray = classInfo.quizzes;
        const quizzesWithClassName = classInfo.quizzes.map((quiz) => ({
          ...quiz,
          class_name: classInfo.class_name,
        }));
        const studentsDataTemp = {};
  
        quizzesArray.forEach((quiz) => {
          quiz.sessions.forEach((session) => {
            const user = session.user;
  
            // Filter only students
            if (user.role !== "student") return;
  
            const userId = user.id;
            const fullName = user.profile
              ? user.profile.full_name
              : user.teacher_profile
              ? user.teacher_profile.full_name
              : user.username; // fallback
  
            // Initialize student data if not already present
            if (!studentsDataTemp[userId]) {
              studentsDataTemp[userId] = {
                id: userId,
                name: fullName,
                quizzes: {}
              };
            }
  
            // Add session attempts
            if (!studentsDataTemp[userId].quizzes[quiz.id]) {
              studentsDataTemp[userId].quizzes[quiz.id] = [];
            }
            studentsDataTemp[userId].quizzes[quiz.id].push({
              attempt: session.attempt,
              final_evaluation: session.final_evaluation,
              session_id: session.id,
            });
          });
        });
  
        setStudentsData(studentsDataTemp);
  
        const quizzesForTableTemp = classInfo.quizzes.map((q) => ({
          id: q.id,
          title: q.title,
        }));
  
        setQuizzesForTable(quizzesForTableTemp);
        setClassData(classInfo);
        setQuizzes(quizzesWithClassName);
      } else {
        toast.error(response.data.message || "Failed to fetch class data.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };  

  useEffect(() => {
    fetchQuizzes();
  }, []);

  const handleCopy = () => {
    navigator.clipboard.writeText(classData?.unique_identifier || "");
    setTitle('Copied!')
  };



  const handleCreateQuiz = () => {
    navigate("/quiz/create", { state: { classId } });
  };

  const handleEditQuiz = (quizId) => {
    navigate(`/quiz-review/${quizId}`);
  };

  const handleDeleteQuiz = (quizId) => {
    setSelectedQuiz(quizId);
    setIsConfirmationModalOpen(true);
  };

  const handleCloseConfirmationModal = () => {
    setIsConfirmationModalOpen(false);
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.delete(`/quizzes/${selectedQuiz}`);
      if (response.data.success) {
        // Update quizzes after deletion
        fetchQuizzes();
        handleCloseConfirmationModal();
      } else {
        toast.error(response.data.message || "Failed to delete quiz.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  const handleViewReportClick = async (sessionId) => {
    try {
      const res = await axiosInstance.get(`/sessions/${sessionId}/report`);
      if (res.data.success) {
        setCurrentReport(res.data.report);
        setReportModalOpen(true);
      } else {
        toast.error(res.data.message || "Failed to fetch AI Report.");
      }
    } catch (err) {
      // Handle specific HTTP errors like 404
      if (err.response) {
        // Server responded with a status code out of the range of 2xx
        const errorMessage = err.response.data?.message || `Error: ${err.response.status}`;
        toast.error(errorMessage);
      } else if (err.request) {
        // No response received from server
        toast.error("No response received from the server. Please try again.");
      } else {
        // Other errors (e.g., client-side issues)
        toast.error(err.message || "An unexpected error occurred.");
      }
    }
  };
  

  const displayedQuizzes = showAll ? quizzes : quizzes.slice(0, initialDisplayCount);

  const totalQuizzes = quizzes.length;
  const isSubset = !showAll && totalQuizzes > displayedQuizzes.length;

  let content;
  if (loading) {
    content = (
      <Box className="loading-container">
        <CircularProgress />
        <Typography variant="h6" className="loading-text">
          Fetching Class Data...
        </Typography>
      </Box>
    );
  } else if (error) {
    content = (
      <Box className="error-container">
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Box>
    );
  } else {
    content = (
      <>
        {/* Class Info Section */}
        <Box className="class-info-card">
          <Box className="class-info-header">
            <Typography variant="h5" className="class-title">
              {classData.class_name}
            </Typography>
            <Box className="class-id-section">
              <Typography variant="body2" className="class-id-title">
                Class ID <br /> <span className="class-id-value">{classData.unique_identifier}</span> 
              </Typography>
              <Tooltip title={title}>
                <IconButton onClick={handleCopy}>
                  <ContentCopyIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Typography variant="body1" className="class-description">
            {classData.description || "No description provided."}
          </Typography>
        </Box>


        {/* Quizzes Section */}
        <ContentHeader
          title="Class Assessments"
          buttonLabel="Create Assessment"
          onButtonClick={handleCreateQuiz}
        />
        <Grid container spacing={3} className="quizzes-grid mb-5">
          {displayedQuizzes.length > 0 ? (
            displayedQuizzes.map((quiz) => (
              <Grid item key={quiz.id}>
                <QuizCard
                  quiz={quiz}
                  onEditClick={() => handleEditQuiz(quiz.id)}
                  onDeleteClick={() => handleDeleteQuiz(quiz.id)}
                />
              </Grid>
            ))
          ) : (
            <Typography>No Assessments available.</Typography>
          )}
        </Grid>
        {(isSubset || (showAll && totalQuizzes > initialDisplayCount)) && (
          <Box display="flex" justifyContent="center" alignItems="center" mt={2} className="mb-5">
            <Typography variant="body2" sx={{ mr: 2 }}>
              {showAll
                ? `Showing all ${totalQuizzes} Assessments`
                : `Showing ${displayedQuizzes.length} of ${totalQuizzes} Assessments`}
            </Typography>
            <Button
              className="text-button"
              onClick={() => setShowAll(!showAll)}
            >
              {showAll ? "View Less" : "View All"}
            </Button>
          </Box>
        )}
        <ContentHeader
          title="Class Grade Book"
        />
        <GradeBookTable
          quizzes={quizzesForTable}
          studentsData={studentsData}
          onViewReport={handleViewReportClick}
        />

        <ReportModal
          open={reportModalOpen}
          onClose={() => setReportModalOpen(false)}
          report={currentReport}
        />
      </>
    );
  }

  return (
    <>
      <Header navLinks={navLinks} isScrollEffect={false} />
      <Container className="class-management-container page-content">
        {content}
      </Container>
      <ConfirmationModal
        open={isConfirmationModalOpen}
        onClose={handleCloseConfirmationModal}
        isDanger={true}
        title="Are you sure you want to delete this Assessment?"
        onSubmit={handleDelete}
        hasNotes={false}
        />
      <ToastContainer />
    </>
  );
}

export default ClassManagementPage;
